import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Globe, Users } from "lucide-react";

interface AccountSubsidiariesTabProps {
  accountId: string;
}

interface ChildCompany {
  id: string;
  company_name: string;
  domain: string | null;
  primary_category: string | null;
  headquarters_city: string | null;
  headquarters_state: string | null;
  headquarters_country: string | null;
  employee_range: string | null;
  icp_tier: string | null;
  contact_count: number;
}

export function AccountSubsidiariesTab({ accountId }: AccountSubsidiariesTabProps) {
  const navigate = useNavigate();

  const { data: children, isLoading } = useQuery({
    queryKey: ["company-subsidiaries", accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_company_children" as any)
        .select("*")
        .eq("parent_company_id", accountId)
        .order("contact_count", { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as ChildCompany[];
    },
  });

  const getLocation = (child: ChildCompany) => {
    const parts = [child.headquarters_city, child.headquarters_state, child.headquarters_country]
      .filter(Boolean);
    return parts.join(", ") || null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!children?.length) {
    return (
      <div className="flex items-center justify-center h-[300px] text-muted-foreground">
        No subsidiaries found
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="text-sm text-muted-foreground mb-4">
        {children.length} {children.length === 1 ? "subsidiary" : "subsidiaries"} &middot;{" "}
        {children.reduce((sum, c) => sum + c.contact_count, 0)} total contacts across subsidiaries
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[250px]">Company</TableHead>
            <TableHead>Domain</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>ICP Tier</TableHead>
            <TableHead className="text-right">Contacts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {children.map((child) => {
            const location = getLocation(child);
            return (
              <TableRow
                key={child.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => navigate(`/accounts/${child.id}`)}
              >
                <TableCell>
                  <span className="font-medium">{child.company_name}</span>
                </TableCell>
                <TableCell>
                  {child.domain ? (
                    <a
                      href={`https://${child.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Globe className="h-3 w-3" />
                      {child.domain}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">&mdash;</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {location || "\u2014"}
                </TableCell>
                <TableCell>
                  {child.icp_tier ? (
                    <Badge variant="outline" className="text-xs">{child.icp_tier}</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">&mdash;</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {child.contact_count > 0 ? (
                    <Badge variant="secondary" className="gap-1">
                      <Users className="h-3 w-3" />
                      {child.contact_count}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">0</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
